import { isAxiosError } from 'axios'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { ExchangeSliceType } from '~/components/exchange/types/exchangeSlice'
import { getProfileIdFromJwt } from '~/lib/utils/getProfileIdFromJwt'
import { externalExchangeClient } from '~/lib/api/externalExchangeClient'
import type { RootState } from '~/store/redux-store'
import { authenticationActions } from './authentication'

function getExchangeBindingErrorMessage(error: unknown) {
  if (isAxiosError(error)) {
    const errorData = error.response?.data

    if (typeof errorData === 'string' && errorData.trim()) return errorData

    if (
      errorData &&
      typeof errorData === 'object' &&
      'message' in errorData &&
      typeof errorData.message === 'string' &&
      errorData.message.trim()
    ) {
      return errorData.message
    }

    if (error.message.trim()) return error.message
  }

  if (error instanceof Error && error.message.trim()) return error.message

  return 'Не удалось загрузить данные для обмена'
}

type GetPointsConverterBindingsIdPayload = { converterBindingId: string; userId: string }

export const getPointsConverterBindingsId = createAsyncThunk<
  GetPointsConverterBindingsIdPayload,
  void,
  { state: RootState; rejectValue: string }
>('exchange/getPointsConverterBindingsId', async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState()
    const userId = getProfileIdFromJwt(state.authentication.sessionToken)

    const response = await externalExchangeClient<string>({
      url: 'v1/bindings/unicredit',
      method: 'POST',
      params: { partner_user_id: userId },
    })

    return { converterBindingId: response.data, userId }
  } catch (error) {
    return rejectWithValue(getExchangeBindingErrorMessage(error))
  }
})

export const initialState: ExchangeSliceType = {
  sourceProgramId: null,
  destinationProgramId: null,
  selectedCardId: null,
  isDisabledFields: false,
  pointsToCharge: 0,
  isValidNow: false,
  hasBeenValid: false,
  converterBindingId: null,
  errorMessage: null,
  userId: null,
}

export const slice = createSlice({
  name: 'exchange',
  initialState,
  reducers: {
    setSourceProgram(state: ExchangeSliceType, { payload }: PayloadAction<string>) {
      state.sourceProgramId = payload
    },
    setDestinationProgram(state: ExchangeSliceType, { payload }: PayloadAction<string>) {
      state.destinationProgramId = payload
    },
    setSelectedCard(state: ExchangeSliceType, { payload }: PayloadAction<string>) {
      state.selectedCardId = payload
    },
    setIsDisabledFields(state: ExchangeSliceType, { payload }: PayloadAction<boolean>) {
      state.isDisabledFields = payload
    },
    setPointsToCharge(state: ExchangeSliceType, { payload }: PayloadAction<number>) {
      state.pointsToCharge = payload
    },
    setIsValidNow(state: ExchangeSliceType, { payload }: PayloadAction<boolean>) {
      state.isValidNow = payload
    },
    setHasBeenValid(state: ExchangeSliceType, { payload }: PayloadAction<boolean>) {
      state.hasBeenValid = payload
    },
    resetExchange(state: ExchangeSliceType) {
      state.isDisabledFields = false
      state.pointsToCharge = 0
      state.isValidNow = false
      state.hasBeenValid = false
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPointsConverterBindingsId.fulfilled, (state: ExchangeSliceType, { payload }) => {
      state.converterBindingId = payload.converterBindingId
      state.userId = payload.userId
      state.errorMessage = null
    })
    builder.addCase(getPointsConverterBindingsId.rejected, (state: ExchangeSliceType, { payload }) => {
      state.errorMessage = payload ?? 'Не удалось загрузить данные для обмена'
    })
    builder.addCase(authenticationActions.logout, () => {
      return initialState
    })
  },
})

export default slice.reducer
export const exchangeActions = slice.actions
